import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import Collapse from '@mui/material/Collapse';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useEffect, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { ReactComponent as CloudQueueIcon } from '../../../../layout/icons/Mornitor.svg';
import { ReactComponent as CrossBrowser } from '../../../../layout/icons/CrossBrowser.svg';
import { ReactComponent as MobileNative } from '../../../../layout/icons/MobileNative.svg';
import { ReactComponent as PrivateDevice } from '../../../../layout/icons/PrivateDevice.svg';
import { formatCurrency } from '../../../../components';
import { Feature, PlanInterval, RecurlyAddOn } from '../../../../models';
import { SelectQuota } from './SelectQuota';
import { fromOrder, useAppDispatch } from '../../../../store';
import { getUnitPrice } from '../../../../hook';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../../../utils/Constants';

const useStyles = makeStyles(theme => ({
  cloudEnvToggleRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    cursor: 'pointer',
  },
  cloudEnvToggleMain: {
    display: 'flex',
    height: '100%',
    width: '100%',
    minWidth: 0,
  },
  cloudEnvToggleIconBox: {
    borderRadius: 4,
    paddingTop: theme.spacing(0.5),
    color: '#4B5563',
    display: 'flex',
    flexShrink: 0,
  },
  cloudEnvToggleText: {
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  cloudEnvToggleTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: '#22283C',
    lineHeight: 1.3,
  },
  cloudEnvToggleIcon: {
    height: '100%',
    fontWeight: 700,
    fontSize: 16,
    color: '#22283C',
    lineHeight: 1.3,
  },
  cloudEnvToggleDesc: {
    fontWeight: 400,
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 1.4,
    paddingTop: theme.spacing(1),
  },
  cloudEnvChevron: {
    color: '#000000',
  },
  cloudEnvToggleFocus: {
    border: 0,
    width: '100%',
    textAlign: 'left',
    '&:focus-visible': {
      outlineOffset: 2,
    },
  },
  addOnCard: {
    backgroundColor: '#ffffff',
    marginBottom: theme.spacing(0),
  },
  addOnHeader: {
    marginBottom: theme.spacing(2),
  },
  addOnSectionLabel: {
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: 1,
    color: '#5959EB',
    textTransform: 'uppercase',
  },
  addOnOptionalLabel: {
    fontWeight: 400,
    fontSize: 12,
    color: '#808B9A',
    marginLeft: theme.spacing(1),
  },
  addOnItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2),
  },
  addOnItemIcon: {
    width: 36,
    height: 36,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#5959EB',
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  addOnItemTitle: {
    fontWeight: 700,
    fontSize: 14,
    color: '#22283c',
  },
  addOnItemDesc: {
    fontWeight: 400,
    fontSize: 13,
    color: '#808B9A',
  },
  addOnItemPrice: {
    fontWeight: 700,
    fontSize: 13,
    color: '#6A6A6A',
  },
  addButton: {
    border: '1px solid #5959EB',
    color: '#5959EB',
    fontWeight: 600,
    borderRadius: 4,
    textTransform: 'none',
    padding: theme.spacing(0.5, 2),
    minWidth: 80,
    '&:hover': {
      backgroundColor: '#EEF2FF',
    },
  },
}));

const TestCloudRow = (
  { id, feature, billingInterval, icon, setCloudEnvExpanded }: {
    id: RecurlyAddOn;
    feature: Feature;
    billingInterval: PlanInterval;
    icon: React.ReactNode;
    setCloudEnvExpanded: (value: boolean) => void;
  },
) => {
  const classes = useStyles();
  const intl = useIntl();
  const { flags } = useLaunchDarkly();
  const price = getUnitPrice(flags?.subscriptionPlanPrice, feature, billingInterval);
  const addOns = useSelector(fromOrder.selectAddOns);
  const [session, setSession] = useState(addOns?.[id.toUpperCase()] ?? 0);
  const dispatch = useAppDispatch();

  const updateAddOnQuota = (addOns: Record<string, number>, addOn: RecurlyAddOn, quota: number) => {
    const key = addOn.toUpperCase();
    const updatedAddOns = { ...addOns };
    updatedAddOns[key] = quota;

    dispatch(fromOrder.setAddOns(updatedAddOns));
  };

  const delayDebounceFn = useCallback(debounce(updateAddOnQuota, 1000), []);

  useEffect(() => {
    if (session > 0) {
      setCloudEnvExpanded(true);
    }
    delayDebounceFn(addOns ?? {}, id, session);
  }, [session]);

  return (
    <Box className={classes.addOnItem}>
      <Box display="flex" alignItems="flex-start" flex={1}>
        <Box className={classes.addOnItemIcon}>
          {icon}
        </Box>
        <Box>
          <Typography className={classes.addOnItemTitle}>
            <FormattedMessage id={`page.plan.v2.addon.${id}.title`} />
          </Typography>
          <Typography className={classes.addOnItemDesc}>
            <FormattedMessage id={`page.plan.v2.addon.${id}.desc`} />
          </Typography>
          <Typography className={classes.addOnItemPrice}>
            <FormattedMessage
              id={billingInterval === PlanInterval.YEAR
                ? 'page.plan.v2.addon.price.annual'
                : 'page.plan.v2.addon.price.month'}
              values={{
                price: formatCurrency(intl, price, DEFAULT_CURRENCY_FORMAT_OPTIONS),
              }}
            />
          </Typography>
        </Box>
      </Box>
      {session === 0
        ? (
          <Button variant="outlined" className={classes.addButton} onClick={() => setSession(1)} startIcon={<AddIcon />}>
            <FormattedMessage id="page.plan.v2.addon.add.button" />
          </Button>
        )
        : (
          <SelectQuota
            productId={id}
            initQuota={0}
            quota={session}
            setQuota={setSession}
            width={32}
            height={32}
          />
        )}
    </Box>
  );
};

export const TestCloudSession = (
  { billingInterval }: {
    billingInterval: PlanInterval;
  },
) => {
  const classes = useStyles();
  const [cloudEnvExpanded, setCloudEnvExpanded] = useState<boolean>(false);

  return (
    <Paper className={classes.addOnCard} elevation={0} sx={{ border: '1px solid #BBBBBB', borderRadius: 2 }}>
      {/* Cloud Execution Environment toggle row */}
      <Button
        id="cloud-env-toggle-button"
        key="cloud-env-toggle-button"
        className={classes.cloudEnvToggleFocus}
        aria-expanded={cloudEnvExpanded}
        onClick={() => setCloudEnvExpanded(prev => !prev)}
        sx={theme => ({
          borderTopRadius: theme.spacing(1),
          borderBottomLeftRadius: cloudEnvExpanded ? theme.spacing(0) : theme.spacing(1),
          borderBottomRightRadius: cloudEnvExpanded ? theme.spacing(0) : theme.spacing(1),
          backgroundColor: '#E4E7F5',
        })}
      >
        <Box className={classes.cloudEnvToggleRow}>
          <Box className={classes.cloudEnvToggleMain}>
            <Box className={classes.cloudEnvToggleIconBox}>
              <CloudQueueIcon fontSize="small" />
            </Box>
            <Box className={classes.cloudEnvToggleText}>
              <Typography className={classes.cloudEnvToggleTitle}>
                <FormattedMessage id="page.plan.v2.cloud.env.toggle" />
              </Typography>
              <Typography className={classes.cloudEnvToggleDesc}>
                <FormattedMessage id="page.plan.v2.cloud.env.toggle.desc" />
              </Typography>
            </Box>
          </Box>
          <Box className={classes.cloudEnvToggleIcon}>
            {cloudEnvExpanded
              ? <ExpandLessIcon className={classes.cloudEnvChevron} />
              : <ExpandMoreIcon className={classes.cloudEnvChevron} />}
          </Box>
        </Box>
      </Button>
      <Collapse in={cloudEnvExpanded}>
        {/* Desktop & Mobile Browser */}
        <TestCloudRow
          id={RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER}
          billingInterval={billingInterval}
          feature={Feature.TESTCLOUD_SESSION_CROSS_BROWSER}
          icon={<CrossBrowser fontSize="small" />}
          setCloudEnvExpanded={setCloudEnvExpanded}
        />

        {/* Mobile Native App */}
        <TestCloudRow
          id={RecurlyAddOn.TESTCLOUD_SESSION_MOBILEAPP}
          billingInterval={billingInterval}
          feature={Feature.TESTCLOUD_SESSION_MOBILE_APP}
          icon={<MobileNative fontSize="small" />}
          setCloudEnvExpanded={setCloudEnvExpanded}
        />

        {/* Private Dedicated Device */}
        <TestCloudRow
          id={RecurlyAddOn.TESTCLOUD_SESSION_PRIVATEDEVICE}
          billingInterval={billingInterval}
          feature={Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE}
          icon={<PrivateDevice fontSize="small" />}
          setCloudEnvExpanded={setCloudEnvExpanded}
        />
      </Collapse>
    </Paper>
  );
};
